import { useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { LineReveal } from "./Reveal";
import StoreBadges from "./StoreBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG =
  "https://images.unsplash.com/photo-1786282405203-f7cbd2529d81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHx2aWJyYW50JTIwZnJlc2glMjBmcnVpdHMlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODY2NzAxNjd8MA&ixlib=rb-4.1.0&q=85&w=900";

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const badgeY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/waitlist`, { email });
      setStatus("done");
      toast.success("You're on the list — see you at launch.");
    } catch {
      setStatus("idle");
      toast.error("Couldn't add you right now. Try again in a moment.");
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen pt-32 md:pt-40 pb-24 px-6 md:px-12 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 md:gap-8 items-end">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="uppercase tracking-[0.2em] text-xs font-semibold text-[#16783F] mb-8"
            data-testid="hero-overline"
          >
            Now available on the App Store &amp; Google Play
          </motion.p>
          <h1
            className="font-serif font-light tracking-tighter leading-[0.92] text-[15vw] md:text-[6.5rem] lg:text-[7.5rem]"
            data-testid="hero-heading"
          >
            <LineReveal delay={0.2}>Freshness,</LineReveal>
            <LineReveal delay={0.38}>
              <em className="text-[#1F9D55]">tracked.</em>
            </LineReveal>
            <LineReveal delay={0.56}>Nothing wasted.</LineReveal>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 max-w-md text-base md:text-lg leading-relaxed text-[#111111]/70"
            data-testid="hero-subcopy"
          >
            My Fresh Tracker watches the expiry dates on your groceries so you don&rsquo;t have to. Scan what you buy,
            get a gentle nudge before anything turns, and eat everything you paid for.
          </motion.p>
          <motion.form
            onSubmit={submit}
            id="hero-notify-form"
            data-testid="hero-notify-form"
            className="mt-12 max-w-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
          >
            <div className="flex items-end gap-3 border-b border-[#111111]/30 focus-within:border-[#1F9D55] transition-colors duration-300 pb-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="hero-email-input"
                className="w-full bg-transparent outline-none text-lg md:text-xl placeholder:text-[#111111]/35 focus:ring-0"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="hero-notify-button"
                className="shrink-0 rounded-full bg-[#16783F] text-[#F7F6F2] px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-2 hover:bg-[#111111] transition-colors duration-300 disabled:opacity-60"
              >
                {status === "loading" ? "Joining" : "Notify me"}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#111111]/50">
              Occasional updates. No spam, ever.
            </p>
          </motion.form>
          {status === "done" && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-5 text-sm font-medium text-[#0A130B]"
              data-testid="hero-success-message"
            >
              You&rsquo;re on the list — we&rsquo;ll keep you posted.
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="mt-12"
          >
            <StoreBadges idPrefix="hero" />
          </motion.div>
        </div>
        <div className="md:col-span-5 relative mt-8 md:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-t-full overflow-hidden border border-[#111111]/10"
          >
            <motion.img
              src={HERO_IMG}
              alt="A halved lemon, spot-lit against a dark background"
              style={{ y: imgY }}
              className="w-full h-[420px] md:h-[560px] object-cover scale-110"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            style={{ y: badgeY }}
            className="absolute -bottom-6 -left-4 md:-left-8 rotate-[-8deg] bg-[#F2C94C] text-[#0A130B] px-6 py-3 rounded-full border border-[#111111]/15"
            data-testid="hero-launch-badge"
          >
            <span className="font-serif italic text-lg md:text-xl">Available now</span>
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 text-[#111111]/50"
        data-testid="hero-scroll-cue"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
        <span className="uppercase tracking-[0.2em] text-xs">Scroll</span>
      </motion.div>
    </section>
  );
}
